import { useUpdateShipments } from "../../../../utils/hooks/commonly-stored/uploaded-board/updates/shipments";
import { ShipmentsList, ShipmentsRow } from "./ShipmentsStyles";
import { InputLabel } from "../../../../constants/Styles";
import NumberInput from "../../../common/NumberInput";
import React, { useState } from "react";
import {
  Board,
  LeadTimeOption,
  Shipment,
} from "../../../../reducers/uploadedBoard";
import { useBoardFromContext } from "../../../../utils/hooks/commonly-stored/uploaded-board/board";
import Button from "../../../common/Button";
import LoadingOverlay from "../../../LoadingOverlay";
import useShipmentsFromContext from "../../../../utils/hooks/commonly-stored/uploaded-board/shipments";
import { ErrorText } from "../QuoteHistory/QuoteHistoryStyles";
import { LineItemDescription } from "../../../common/LineItem/LineItemStyles";
import { useAppSelector } from "../../../../utils/hooks/store";
import {
  dateStringToHumanReadableDate,
  dateToHumanReadableDate,
} from "../../../../utils/valueFormatting";
import useGetShipDate from "../../../../utils/hooks/shipDate";
import { ProcessingPill } from "../../ProjectDetails";

// Engineers can set any lead time; the backend only requires it to be positive.
const ENGINEER_MIN_LEAD_TIME_DAYS = 0;

function getFastestOption(shipment: Shipment): LeadTimeOption | null {
  // Each option's days already include this design's lead time adjustment,
  // so the fastest enabled option is the floor for customers.
  return shipment.lead_time_options.options
    .filter((option) => !option.disabled)
    .reduce<LeadTimeOption | null>((fastest, option) => {
      if (fastest === null || option.days < fastest.days) {
        return option;
      }
      return fastest;
    }, null);
}

export default function AddOrEditShipment({
  onUpdate,
  shipmentId,
}: {
  onUpdate: () => void;
  shipmentId?: string;
}) {
  const { addShipment, addingShipment, updateShipmentDetails } =
    useUpdateShipments();
  const board = useBoardFromContext().board as Board;
  const shipments = useShipmentsFromContext(board.id) as Shipment[];
  const isAdminOrEngineer = useAppSelector(
    (state) => state.auth.isAdminOrEngineer,
  );
  const lastShipment = shipmentId
    ? (shipments.find((shipment) => shipment.id == shipmentId) as Shipment)
    : (shipments.at(-1) as Shipment);
  const [numBoards, setNumBoards] = useState(lastShipment.num_boards);
  const [leadTimeDays, setLeadTimeDays] = useState(lastShipment.lead_time_days);
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null,
  );

  const fastestOption = getFastestOption(lastShipment);
  const minLeadTimeDays =
    isAdminOrEngineer || fastestOption === null
      ? ENGINEER_MIN_LEAD_TIME_DAYS
      : fastestOption.days;
  const leadTimeTooShort = leadTimeDays < minLeadTimeDays;

  const { shipDate } = useGetShipDate(
    leadTimeDays,
    lastShipment.date_lead_time_started,
  );

  const handleUpdateShipment = () => {
    if (!numBoards) {
      setValidationMessage("Number of boards must be a positive number.");
    } else if (leadTimeTooShort) {
      setValidationMessage(
        `The fastest lead time available for this design is ${minLeadTimeDays.toLocaleString()} days.`,
      );
    } else if (shipmentId) {
      updateShipmentDetails(
        shipmentId,
        {
          num_boards: numBoards,
          lead_time_days: leadTimeDays,
        },
        onUpdate,
      );
    } else {
      addShipment(
        {
          num_boards: numBoards,
          lead_time_days: leadTimeDays,
        },
        onUpdate,
      );
    }
  };

  return (
    <ShipmentsList>
      <LoadingOverlay loading={addingShipment} text={"Updating shipment..."} />
      {validationMessage && <ErrorText>{validationMessage}</ErrorText>}
      <ShipmentsRow>
        <div>
          <InputLabel>Number of boards</InputLabel>
          <NumberInput
            name="number of boards"
            value={numBoards}
            onChange={(newValue) => {
              setNumBoards(newValue);
            }}
            min={0}
          />
        </div>
        <div>
          <InputLabel>Lead time (business days)</InputLabel>
          <NumberInput
            name="lead time"
            value={leadTimeDays}
            onChange={(newValue) => {
              setValidationMessage(null);
              setLeadTimeDays(newValue);
            }}
            min={minLeadTimeDays}
          />
        </div>
        <Button
          label={"Save"}
          onClick={handleUpdateShipment}
          disabled={numBoards === 0 || leadTimeTooShort}
        />
      </ShipmentsRow>
      <div>
        <InputLabel>Ship date</InputLabel>
        {shipDate === null ? (
          <ProcessingPill />
        ) : (
          <div>{dateToHumanReadableDate(shipDate, true)}</div>
        )}
      </div>
      {!isAdminOrEngineer && fastestOption !== null && (
        <LineItemDescription>
          The fastest lead time for this design is{" "}
          {fastestOption.days.toLocaleString()} business days, shipping{" "}
          {dateStringToHumanReadableDate(fastestOption.ship_date as string)}.
        </LineItemDescription>
      )}
      <LineItemDescription>
        <a href={"/capabilities#lead-time"} target="_blank" rel="noreferrer">
          See more information
        </a>
      </LineItemDescription>
    </ShipmentsList>
  );
}
